import type { CSSProperties, ReactElement, ReactNode, RefObject } from "react"
import { useRef, useState } from "react"

import SearchIcon from "@mui/icons-material/Search"
import { Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, Menu, MenuItem, Skeleton, Typography } from "@synapse-analytics/synapse-ui"
import type { TooltipWrapperProps } from "@synapse-analytics/synapse-ui/build/components/Tooltip/ToolTip"

import { getTheme } from "../../../hooks/UseTheme"
import { useDebounce } from "../../../hooks/useDebounce"
import { CustomArrowAdornment } from "../../UI/CustomArrowAdornment"

import styles from "./SelectWithSearch.module.scss"

export type MenuOption = {
  value: string
  text: string
  item: ReactNode
  disabled?: boolean
  listItemStyles?: CSSProperties
}

type SearchSelectInput = {
  inputValue?: string
  searchInputValue?: string
  optionsList: MenuOption[]
  searchInputPlaceholder?: string
  isOptionsLoading?: boolean
  lastMenuItemRef?: RefObject<HTMLDivElement> | ((node?: Element | null) => void)
  hideSearchbox?: boolean
  debounceTimer?: number
  searchInputChangeHandler?: (e: InputChangeEvent) => void
  onSelectMenuItem?: (params: MenuOption) => void
  onMenuClose?: () => void
  menuTriggerProps?: {
    placeholder?: string
    label?: string
    hideDescription?: boolean
    labelTooltipProps?: Omit<TooltipWrapperProps, "children">
    disabled?: boolean
    fullWidth?: boolean
  }
  customMenuAction?: (onClick: () => void) => ReactElement
  customMenuTrigger?: (inputRef: RefObject<HTMLInputElement>, onClick: () => void) => ReactElement
}

/**
 * A select input component with a search bar.
 *
 * @param inputValue - The value of the input
 * @param searchInputValue - The value of the search input
 * @param placeholder - The placeholder text for the input
 * @param label - The label of the input
 * @param disabled - Whether or not the component is disabled
 * @param fullWidth - Whether or not the component takes up the full width
 * @param hideDescription - Whether or not the component should hide the description
 * @param labelTooltipProps - The props for the tooltip of the label
 * @param searchInputPlaceholder - The placeholder text for the search bar
 * @param menuOptionsList - The list of options for the menu
 * @param lastMenuItemRef - The ref for the last menu item
 * @param isOptionsLoading - Whether or not the options are loading
 * @param hideSearchbox - Whether or not the search bar should be hidden
 * @param searchInputChangeHandler - A function to call when the search input changes
 * @param onSelectMenuItem - A function to call when a menu item is selected
 * @param onMenuClose - A function that is called when the menu is closed
 * @returns {ReactElement} The rendered component
 */
export function SelectWithSearch(props: SearchSelectInput): ReactElement {
  const {
    inputValue,
    searchInputValue,
    searchInputPlaceholder,
    optionsList,
    lastMenuItemRef,
    isOptionsLoading,
    hideSearchbox,
    debounceTimer = 300,
    searchInputChangeHandler,
    onSelectMenuItem,
    onMenuClose,
    menuTriggerProps,
    customMenuAction,
    customMenuTrigger,
  } = props

  const theme = getTheme()

  const inputRef = useRef<HTMLInputElement>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)

  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null)

  const inputClickHandler = (): void => {
    if (!!anchorEl) {
      setAnchorEl(null)
      searchInputRef.current?.focus()
      onMenuClose?.()
    } else {
      setAnchorEl(inputRef.current)
      inputRef?.current?.focus()
    }
  }

  const searchInputHandler = (e: InputChangeEvent): void => {
    searchInputChangeHandler?.(e)
  }

  const debouncedSearchInputHandler = useDebounce(searchInputHandler, debounceTimer)

  return (
    <div>
      {customMenuTrigger ? (
        customMenuTrigger(inputRef, inputClickHandler)
      ) : (
        <InputText
          isSelect
          endAdornment={<CustomArrowAdornment isMenuOpen={!!anchorEl} handleAdornmentClick={inputClickHandler} />}
          handleInputClick={inputClickHandler}
          menuProps={{ menuMaxContent: true }}
          anchoringRef={inputRef}
          value={inputValue}
          placeholder={menuTriggerProps?.placeholder}
          label={menuTriggerProps?.label}
          disabled={menuTriggerProps?.disabled}
          hideDescription={menuTriggerProps?.hideDescription}
          labelTooltipProps={menuTriggerProps?.labelTooltipProps}
          fullWidth={menuTriggerProps?.fullWidth}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => {
          setAnchorEl(null)
          onMenuClose?.()
        }}
        menuMaxContent
      >
        {customMenuAction && (
          <div className={styles.customAction}>
            {customMenuAction(() => {
              setAnchorEl(null)
              onMenuClose?.()
            })}
          </div>
        )}

        {!hideSearchbox && (
          <div className={styles.searchbar}>
            <InputText
              value={searchInputValue}
              id="search-bar"
              width={100}
              fullWidth
              startAdornment={
                <SearchIcon fontSize="small" style={{ marginTop: "2px", color: theme.palette.base.text[2] }} />
              }
              placeholder={searchInputPlaceholder ?? "Search.."}
              handleChange={debouncedSearchInputHandler}
              variant="filled"
              hideDescription
              customRef={searchInputRef}
            />
          </div>
        )}

        <div className={styles.optionsContainer}>
          {optionsList.length === 0 && !isOptionsLoading ? (
            <Grid item height="100%" mt={1} container justifyContent="center" alignItems="center">
              <Typography variantColor={2} variant="h3-bold">
                No results found
              </Typography>
            </Grid>
          ) : (
            optionsList.map((option, index, arr) => (
              <MenuItem
                style={option.listItemStyles}
                disabled={option.disabled}
                value={option.value}
                ref={index === arr.length - 1 ? lastMenuItemRef : undefined}
                onClick={() => {
                  onSelectMenuItem?.(option)
                  setAnchorEl(null)
                }}
                isSelected={option.text === inputValue}
              >
                <div className={styles.optionLabel} title={option.text}>
                  {option.item}
                </div>
              </MenuItem>
            ))
          )}
          {isOptionsLoading && (
            <Grid item height="100%" container justifyContent="center" alignItems="center">
              <Skeleton width="100%" height="30px" variant="rectangular" />
            </Grid>
          )}
        </div>
      </Menu>
    </div>
  )
}
